import { JSON_REQUEST_HEADER } from '@/helpers/utilities'

/**
 * Cliente HTTP del POS.
 *
 * ¿Por que no `useRequest()` (@/helpers/hooks/useCrudOpration)? Porque devuelve
 * `response.json()` a secas y TIRA el codigo HTTP. El POS mueve dinero y factura:
 * necesita distinguir
 *   401 sin sesion  /  403 sin permiso  /  402 cobro no capturado  /  422 negocio,
 * y ademas 401/419 responden `{message: "..."}` SIN la clave `status`, asi que
 * mirar solo el body no basta.
 *
 * Se conserva el patron del modulo: mismas cabeceras/CSRF (JSON_REQUEST_HEADER),
 * misma cookie de sesion (fetch same-origin por defecto => Sanctum stateful, tal
 * como autentica hoy el flujo de reserva), y misma forma de URL `{path, method}`.
 *
 * SIEMPRE resuelve (nunca lanza): devuelve un resultado normalizado para que
 * ningun error del envelope se pierda por el camino.
 *
 * @returns {Promise<{ok: boolean, httpStatus: number, data: any, message: string, errors: object}>}
 */
export const posRequest = async (URL, body = null) => {
  let response

  try {
    const options = {
      method: URL.method,
      headers: JSON_REQUEST_HEADER(),
      credentials: 'same-origin'
    }

    if (body !== null && URL.method !== 'GET') {
      options.body = JSON.stringify(body)
    }

    response = await fetch(URL.path, options)
  } catch (e) {
    // Red caida / DNS / CORS: jamas se traga en silencio.
    return { ok: false, httpStatus: 0, data: null, message: e?.message || 'Network error', errors: {} }
  }

  let payload = null

  try {
    payload = await response.json()
  } catch (e) {
    // 500 con pagina HTML, 419 con redirect, respuesta vacia...
    payload = null
  }

  if (payload === null) {
    return {
      ok: false,
      httpStatus: response.status,
      data: null,
      message: `HTTP ${response.status}`,
      errors: {}
    }
  }

  // Envelope de la app: {status, data, message}. `status:false` => error de negocio.
  const envelopeOk = response.ok && payload.status !== false

  return {
    ok: envelopeOk,
    httpStatus: response.status,
    data: payload.data ?? null,
    // `message` del envelope; si no lo hay (401/419 de Laravel), su propio message.
    message: payload.message ?? '',
    // En 422 el backend mete los errores de validacion en `data`.
    errors: response.status === 422 && payload.data && typeof payload.data === 'object' ? payload.data : {}
  }
}

/**
 * Aplana los errores de validacion de Laravel ({campo: [msg, ...]}) a una lista
 * legible, para poder mostrarlos sin perder ninguno.
 */
export const flattenErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return []

  return Object.values(errors)
    .flat()
    .filter((msg) => typeof msg === 'string')
}
